import { useCallback } from 'react';
import { isDynamicReport, isStaticReport } from '../../campaigns/config/isStaticDynamicReport';
import { useDynamicReportCache } from '../../scheduled-reports/hooks/useDynamicReportCache';
import { useDBStorage } from './DBStorage/useDBStorage';
import { useReportStorageReady } from './useReportStorageReady';

const parseReportId = key => {
    const [accountId, reportId, dynamicReportPropName] = `${key}`.split('.');

    return { accountId, reportId, dynamicReportPropName };
};

export const useReportStorage = () => {
    const [reportStorageReady] = useReportStorageReady();
    const { getter: dbStorageGetter, setter: dbStorageSetter, isReady: isDBReady } = useDBStorage();
    const { getter: dynamicReportGetter, setter: dynamicReportSetter } = useDynamicReportCache();

    const getter = useCallback(
        key => {
            const { accountId, reportId, dynamicReportPropName } = parseReportId(key);
            if (!reportId) {
                return;
            }
            if (isStaticReport(reportId) && isDBReady) {
                return dbStorageGetter(key);
            }

            if (isDynamicReport(reportId) && reportStorageReady) {
                const value = dynamicReportGetter({ accountId, reportId, dynamicReportPropName });
                return value;
            }
        },
        [isDBReady, reportStorageReady, dbStorageGetter, dynamicReportGetter]
    );
    const setter = useCallback(
        (key, value) => {
            const { accountId, reportId, dynamicReportPropName } = parseReportId(key);
            if (!reportId) {
                return;
            }

            if (isStaticReport(reportId) && isDBReady) {
                return dbStorageSetter(key, value);
            }

            if (isDynamicReport(reportId) && reportStorageReady) {
                dynamicReportSetter({
                    accountId,
                    reportId,
                    dynamicReportPropName,
                    value,
                });
            }
        },
        [isDBReady, reportStorageReady, dbStorageSetter, dynamicReportSetter]
    );

    return { getter, setter, ready: reportStorageReady };
};
